import React, { useState, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Box, Button, Icon, Text } from '@deseretbook/react-ui'
import { InputField, TextareaField } from '../../../../dbWebUI'
import { HIGHLIGHT_COLOR_OPTIONS } from '../../../../utils/constants'
import { formatDateString } from '../../../../utils/base'
import { useDropdown } from '@deseretbook/react-utils'
import HighlightOptions from './HighlightOptions'
import defaultHighlight from '../../../../utils/defaultHighlight'

import './NoteView.css'

const NoteView = ({
  note: initialNote,
  onSave,
  onSaveHighlight,
  onBack,
  isLoading,
  className,
  annotation,
  ...rest
}) => {
  const highlight = annotation.highlight || defaultHighlight()
  const highlightRef = useRef()

  const highlightColor =
    HIGHLIGHT_COLOR_OPTIONS.find((color) => color.value === highlight.color) ||
    HIGHLIGHT_COLOR_OPTIONS[0]

  const { dropdownProps, triggerProps } = useDropdown()

  const [isEditing, setIsEditing] = useState(!initialNote.title && !initialNote.text)
  const [isSaving, setIsSaving] = useState(false)
  const [activeButton, setActiveButton] = useState(null)
  const [note, setNote] = useState(initialNote)

  // Handlers
  const onClickHighlightHandler = useCallback(
    (key, value) => {
      if (isLoading || highlight[key] === value) return
      setActiveButton(value)
      onSaveHighlight({ [key]: value })
    },
    [isLoading, highlight, onSaveHighlight],
  )

  const onChangeHandler = useCallback(
    (e) => {
      if (isLoading) return
      const { name, value } = e.target
      setNote((prevNote) => ({
        ...prevNote,
        [name]: value,
      }))
    },
    [isLoading],
  )

  const handleSave = useCallback(() => {
    if (isLoading || !(note.text || note.title)) return
    setIsSaving(true)
    onSave(note, false, onBack)
    dropdownProps.close()
  }, [isLoading, note, onSave, onBack, dropdownProps])

  const onSubmitHandler = useCallback(
    (e) => {
      e.preventDefault()
      handleSave()
    },
    [handleSave],
  )

  const toggleEdit = useCallback(() => {
    setIsEditing((prevEditing) => !prevEditing)
    setNote(initialNote)
  }, [initialNote])

  const deleteNote = useCallback(() => {
    if (isLoading || !initialNote.id) return
    setIsSaving(true)
    onSave({}, true, onBack)
  }, [isLoading, initialNote, onSave, onBack])

  const hasContent = Boolean(initialNote.text || initialNote.title)
  const canSave = Boolean(note.text || note.title) && !isSaving
  const classes = classNames('note-view-container', className)
  const lastUpdated = formatDateString(annotation.changed || annotation.created, true)

  // Handle closing the dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        highlightRef.current &&
        !highlightRef.current.contains(event.target) &&
        dropdownProps.triggerRef.current &&
        !dropdownProps.triggerRef.current.contains(event.target)
      ) {
        dropdownProps.close()
      }
    }

    if (dropdownProps.isOpen) {
      document.addEventListener('mouseup', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mouseup', handleClickOutside)
    }
  }, [dropdownProps, dropdownProps.isOpen, dropdownProps.close, dropdownProps.triggerRef])

  // Extracted inline styles
  const closeIconStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  }

  return (
    <Box id="NoteView" className={classes} {...rest} isColumn alignItems="center">
      <Box
        className="note-view-navbar"
        isFullwidth
        alignItems="center"
        justifyContent="space-between"
        style={{ backgroundColor: highlightColor.hex }}
      >
        <Button {...triggerProps} className="note-view-color note-view-button">
          <Icon name="colorWheel" className="note-view-icon" />
        </Button>
        <Text className="note-view-header">{note.title}</Text>
        <Button className="note-view-close" onClick={isEditing && hasContent ? toggleEdit : onBack}>
          <Icon style={closeIconStyle} name="close" />
        </Button>
      </Box>

      <Box isFullwidth style={{ padding: '20px' }}>
        {isEditing ? (
          <form onSubmit={onSubmitHandler} isSubmitting={isSaving} isValid={!isSaving && canSave}>
            <InputField
              id="title"
              name="title"
              labelContent="Title"
              onChange={onChangeHandler}
              defaultValue={note.title}
              autoComplete="off"
              autoFocus
              maxLength="20"
            />
            <TextareaField
              id="text"
              name="text"
              labelContent="Note"
              onChange={onChangeHandler}
              defaultValue={note.text}
              autoComplete="off"
              rows="3"
            />
          </form>
        ) : (
          <Box className="note-view-content">
            <Text style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '16px' }}>
              {note.text}
            </Text>
          </Box>
        )}
      </Box>

      <Box
        className="note-view-footer"
        alignItems="center"
        justifyContent="space-between"
        isFullwidth
      >
        <Text className="note-view-date">{lastUpdated}</Text>
        <Box alignItems="center">
          <Button
            disabled={isEditing}
            onClick={deleteNote}
            className="note-view-button note-view-delete"
          >
            Delete
          </Button>
          <Button
            onClick={isEditing ? handleSave : toggleEdit}
            className={`note-view-button note-view-${isEditing ? 'save' : 'edit'}`}
          >
            {isEditing ? 'Save' : 'Edit'}
          </Button>
        </Box>
      </Box>

      <Box
        {...dropdownProps}
        ref={highlightRef}
        className="note-view-colors"
        style={{ display: dropdownProps.isOpen ? 'block' : 'none' }}
      >
        <HighlightOptions
          highlight={highlight}
          isLoading={isLoading}
          activeButton={activeButton}
          onClick={onClickHighlightHandler}
        />
      </Box>
    </Box>
  )
}

NoteView.defaultProps = {
  isLoading: false,
  note: {},
  onSave: () => {},
  onBack: () => {},
  className: '',
}

NoteView.propTypes = {
  /** True if an action is taking place. */
  isLoading: PropTypes.bool,
  /** Annotation note object. */
  note: PropTypes.shape({
    title: PropTypes.string,
    text: PropTypes.string,
  }),
  /** Callback for when a note should be saved. */
  onSave: PropTypes.func,
  /** Callback for when the view should be changed back to the previous view. */
  onBack: PropTypes.func,
  /** Additional class name. */
  className: PropTypes.string,
}

export default NoteView
